/**
 * Mock data configuration
 * Controls whether the app uses mock data or real API calls
 */
import { HTTPError, NormalizedOptions } from "ky";

export function isMockEnabled(): boolean {
  return import.meta.env.VITE_TEST_MODE === "true";
}

export function isDemoRecording(): boolean {
  return isMockEnabled() && import.meta.env.VITE_DEMO_RECORDING === "true";
}

// The first contacts are the "My inbox" threads every tutorial opens; attention
// banners (appointment outcome, knowledge-base draft) are seeded past them so
// those threads stay clean and each banner gets its own tutorial contact.
export const TUTORIAL_CONTACT_COUNT = 4;
export const OUTCOME_FIXTURE_CONTACT_OFFSET = TUTORIAL_CONTACT_COUNT;
export const KNOWLEDGE_SUGGESTION_CONTACT_OFFSET = TUTORIAL_CONTACT_COUNT + 6;

export const MOCK_CONFIG = {
  // Simulated network delay range (ms)
  minDelay: 100,
  maxDelay: 400,
  // Data quantities
  contactCount: 35,
  userCount: 8,
  doctorCount: 5,
  treatmentCount: 12,
  departmentCount: 6,
  productCount: 25,
  orderCount: 20,
  ticketCount: 15,
  snippetCount: 8,
  tagCount: 10,
  channelCount: 5,
} as const;

/**
 * Simulate network delay for mock API calls
 */
export async function simulateDelay(): Promise<void> {
  const delay =
    MOCK_CONFIG.minDelay +
    Math.random() * (MOCK_CONFIG.maxDelay - MOCK_CONFIG.minDelay);

  await new Promise((resolve) => setTimeout(resolve, delay));
}

const MOCK_REQUEST_URL = "https://api.mock";
// `HTTPError` only stores its options, so the smallest valid set stands in.
const MOCK_REQUEST_OPTIONS: NormalizedOptions = {
  method: "GET",
  retry: {},
  prefixUrl: "",
  onDownloadProgress: undefined,
  onUploadProgress: undefined,
  context: {},
};

export const mockHttpError = (status: number): HTTPError =>
  new HTTPError(
    new Response(null, { status }),
    new Request(MOCK_REQUEST_URL),
    MOCK_REQUEST_OPTIONS,
  );

/** What the API answers for a record that is missing or outside the role's contact wall. */
export const mockNotFound = (): HTTPError => mockHttpError(404);
